import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, Divider, Avatar, Snackbar,
} from '@mui/material';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DataTable from '../../components/common/DataTable';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const redeemableItems = [
  { id: 1, key: 'credit_report', name: 'Free Credit Report', description: 'Get a comprehensive credit report from CTOS or CCRIS', tokens: 30, icon: CreditScoreIcon, color: '#1B365D', stock: 'Available', category: 'Service' },
  { id: 2, key: 'voucher_rm10', name: 'Cash Voucher RM10', description: 'RM10 cash voucher for popular retailers', tokens: 20, icon: CardGiftcardIcon, color: '#00B894', stock: 'Available', category: 'Voucher' },
  { id: 3, key: 'voucher_rm25', name: 'Cash Voucher RM25', description: 'RM25 cash voucher for popular retailers', tokens: 45, icon: CardGiftcardIcon, color: '#00B894', stock: 'Available', category: 'Voucher' },
  { id: 4, key: 'voucher_rm50', name: 'Cash Voucher RM50', description: 'RM50 cash voucher for popular retailers', tokens: 85, icon: CardGiftcardIcon, color: '#00B894', stock: 'Limited', category: 'Voucher' },
  { id: 5, key: 'setel_rm10', name: 'Setel e-Wallet RM10', description: 'RM10 credit to your Setel e-wallet', tokens: 20, icon: AccountBalanceWalletIcon, color: '#E65100', stock: 'Available', category: 'E-Wallet' },
  { id: 6, key: 'setel_rm25', name: 'Setel e-Wallet RM25', description: 'RM25 credit to your Setel e-wallet', tokens: 45, icon: AccountBalanceWalletIcon, color: '#E65100', stock: 'Available', category: 'E-Wallet' },
  { id: 7, key: 'grab_rm15', name: 'Grab Voucher RM15', description: 'RM15 Grab ride or food voucher', tokens: 30, icon: LocalOfferIcon, color: '#2E7D32', stock: 'Available', category: 'Voucher' },
  { id: 8, key: 'tng_rm10', name: 'TnG eWallet RM10', description: 'RM10 Touch n Go eWallet reload', tokens: 20, icon: AccountBalanceWalletIcon, color: '#0D47A1', stock: 'Out of Stock', category: 'E-Wallet' },
];

const historyColumns = [
  {
    field: 'createdAt', headerName: 'Date', width: 140,
    renderCell: (row) => (
      <Typography variant="caption">{new Date(row.createdAt).toLocaleString()}</Typography>
    ),
  },
  { field: 'itemName', headerName: 'Item' },
  {
    field: 'tokensSpent', headerName: 'Tokens Used', align: 'right',
    renderCell: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#C62828' }}>-{row.tokensSpent}</Typography>
    ),
  },
  {
    field: 'status', headerName: 'Status',
    renderCell: (row) => (
      <Chip label={row.status} size="small" sx={{
        backgroundColor: row.status === 'completed' ? '#E8F5E9' : '#FFF8E1',
        color: row.status === 'completed' ? '#2E7D32' : '#F57F17', fontWeight: 600, textTransform: 'capitalize',
      }} />
    ),
  },
  { field: 'id', headerName: 'Reference' },
];

export default function RedemptionCenter() {
  const { user, updateUser } = useAuth();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [refreshKey, setRefreshKey] = useState(0);

  // Live token balance from db (not the stale auth context)
  const liveUser = useMemo(() => {
    if (!user?.id) return null;
    return db.users.getById(user.id);
  }, [user, refreshKey]);

  const tokenBalance = Number(liveUser?.tokenBalance) || 0;

  // Live redemption history
  const redemptionHistory = useMemo(() => {
    if (!user?.id) return [];
    return db.redemptions.getByUserId(user.id);
  }, [user, refreshKey]);

  const handleRedeem = (item) => {
    setSelectedItem(item);
    setShowConfirm(true);
  };

  const confirmRedeem = () => {
    if (!selectedItem || !user?.id) {
      setShowConfirm(false);
      return;
    }
    const result = db.engine.redeemTokens(user.id, selectedItem, user);
    if (!result.success) {
      setSnack({ open: true, msg: result.error || 'Redemption failed', severity: 'error' });
      setShowConfirm(false);
      return;
    }
    // Sync token balance back to auth context so other pages refresh
    updateUser?.({ tokenBalance: result.newBalance });
    setShowConfirm(false);
    setShowSuccess(true);
    setRefreshKey((k) => k + 1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B365D' }}>Redemption Center</Typography>
          <Typography variant="body2" color="text.secondary">Redeem your tokens for exciting rewards</Typography>
        </Box>
        <Card sx={{ borderRadius: 2, backgroundColor: '#1B365D', color: 'white', px: 3, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon />
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Token Balance</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{tokenBalance}</Typography>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Redeemable Items */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {redeemableItems.map((item) => {
          const Icon = item.icon;
          const canAfford = tokenBalance >= item.tokens;
          const inStock = item.stock !== 'Out of Stock';
          return (
            <Grid item xs={12} sm={6} md={3} key={item.id}>
              <Card sx={{
                borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                opacity: (!canAfford || !inStock) ? 0.6 : 1,
                '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
                transition: 'all 0.2s',
              }}>
                <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Avatar sx={{ backgroundColor: `${item.color}15`, width: 48, height: 48 }}>
                      <Icon sx={{ color: item.color }} />
                    </Avatar>
                    {item.stock === 'Limited' && (
                      <Chip label="Limited" size="small" sx={{ backgroundColor: '#FFF8E1', color: '#F57F17', fontWeight: 600, fontSize: '0.65rem' }} />
                    )}
                    {!inStock && (
                      <Chip label="Out of Stock" size="small" sx={{ backgroundColor: '#FFEBEE', color: '#C62828', fontWeight: 600, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{item.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, flex: 1 }}>{item.description}</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip label={`${item.tokens} tokens`} size="small" sx={{ fontWeight: 700, backgroundColor: '#E3F2FD', color: '#1565C0' }} />
                    <Button
                      size="small" variant="contained"
                      disabled={!canAfford || !inStock}
                      onClick={() => handleRedeem(item)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem', backgroundColor: '#00B894' }}
                    >
                      Redeem
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Redemption History */}
      <DataTable columns={historyColumns} rows={redemptionHistory} title="Redemption History" />

      {/* Confirm Dialog */}
      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Redemption</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, backgroundColor: `${selectedItem.color}15` }}>
                <selectedItem.icon sx={{ fontSize: 32, color: selectedItem.color }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedItem.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{selectedItem.description}</Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Token Cost</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedItem.tokens} tokens</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Remaining Balance</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{tokenBalance - selectedItem.tokens} tokens</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setShowConfirm(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={confirmRedeem}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: '#00B894' }}>
            Confirm Redemption
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onClose={() => setShowSuccess(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: '#00B894', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Redemption Successful!</Typography>
          <Typography variant="body2" color="text.secondary">
            Your {selectedItem?.name} will be processed shortly. Check your email for details.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" onClick={() => setShowSuccess(false)}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: '#1B365D' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
